import React, { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppTitleBar } from "../../hooks/useAppTitleBar";
import { useWidgetsFromSession } from "../../hooks/useAppSessionData";
import {
  Page,
  Card,
  TextField,
  IndexTable,
  Text,
  Badge,
  EmptyState,
  Box,
  InlineStack,
  BlockStack,
  Icon,
  Popover,
  ActionList,
  Button,
} from "@shopify/polaris";
import { SearchIcon, FilterIcon } from "@shopify/polaris-icons";
import { useAppNavigate } from "../../hooks/useAppNavigate";
import { APP_DISPLAY_NAME } from "../../config/appBranding";
import PageLoader from "../../components/PageLoader";
import PlanLockOverlay from "../../components/PlanLockOverlay";
import { useBilling } from "../../context/BillingContext";
import { pickPublishedEventId } from "../../utils/planAccess";
import {
  EVENT_FILTER_OPTIONS,
  flattenWidgetsToEvents,
  filterEventRows,
  searchEventRows,
  formatEventDateRange,
} from "../../utils/widgetEventsIndex";

function filterLabel(value) {
  return EVENT_FILTER_OPTIONS.find((o) => o.value === value)?.label || "Show all events";
}

export default function AllEventsPage() {
  const navigate = useAppNavigate();
  const location = useLocation();
  const { widgets, loading } = useWidgetsFromSession();
  const billing = useBilling();
  const publishedEventId = billing.isFreePlan ? pickPublishedEventId(widgets) : null;
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [filterOpen, setFilterOpen] = useState(false);

  const preservedSearch = useMemo(
    () => location.search || window.location.search || "",
    [location.search],
  );

  const allRows = useMemo(() => flattenWidgetsToEvents(widgets), [widgets]);

  const visibleRows = useMemo(() => {
    let rows = filterEventRows(allRows, filter);
    rows = searchEventRows(rows, search);
    return rows.sort((a, b) => {
      const ta = a.startAt ? new Date(a.startAt).getTime() : 0;
      const tb = b.startAt ? new Date(b.startAt).getTime() : 0;
      return tb - ta;
    });
  }, [allRows, filter, search]);

  const onCreateWidget = useCallback(() => {
    navigate({ pathname: "/widgets/new", search: preservedSearch });
  }, [navigate, preservedSearch]);

  useAppTitleBar({
    title: "All events",
    primaryAction: { label: "Create widget", onClick: onCreateWidget },
  });

  const goToPlans = useCallback(() => {
    navigate({ pathname: "/plans", search: preservedSearch });
  }, [navigate, preservedSearch]);

  const onEditEvent = useCallback(
    (row) => {
      if (!row?.widgetId || !row?.eventId) return;
      const params = new URLSearchParams(preservedSearch.replace(/^\?/, ""));
      params.set("event", row.eventId);
      navigate({
        pathname: `/widgets/${encodeURIComponent(row.widgetId)}/edit`,
        search: params.toString() ? `?${params.toString()}` : "",
      });
    },
    [navigate, preservedSearch],
  );

  const filterItems = EVENT_FILTER_OPTIONS.map((opt) => ({
    content: opt.label,
    active: filter === opt.value,
    onAction: () => {
      setFilter(opt.value);
      setFilterOpen(false);
    },
  }));

  const rowMarkup = visibleRows.map((row, index) => {
    const locked = Boolean(publishedEventId && String(row.eventId) !== String(publishedEventId));
    const title = (
      <Text as="span" variant="bodyMd" fontWeight="semibold">
        {row.title}
      </Text>
    );
    const date = (
      <Text as="span" variant="bodySm">
        {formatEventDateRange(row.startAt, row.endAt)}
      </Text>
    );
    const status = row.hidden ? (
      <Badge tone="attention">Draft</Badge>
    ) : (
      <Badge tone="success">Published</Badge>
    );
    const details = (
      <BlockStack gap="100">
        <Text as="span" variant="bodySm">
          {row.widgetName}
        </Text>
        {row.addRegistration ? (
          <Text as="span" variant="bodySm" tone="subdued">
            Registration form
          </Text>
        ) : (
          <Text as="span" variant="bodySm" tone="subdued">
            No registration
          </Text>
        )}
        {row.recurring ? (
          <Text as="span" variant="bodySm" tone="subdued">
            Recurring
          </Text>
        ) : null}
      </BlockStack>
    );
    if (locked) {
      return (
        <IndexTable.Row
          id={`plan-locked-${row.widgetId}-${row.eventId}`}
          key={`${row.widgetId}-${row.eventId}`}
          position={index}
          onClick={goToPlans}
        >
          <IndexTable.Cell colSpan={5}>
            <PlanLockOverlay locked onUnlock={goToPlans} label="Upgrade to Pro to edit this event">
              <div className="age-plan-lock__event">
                <div>{title}</div>
                <div>{date}</div>
                <div>{status}</div>
                <div>{details}</div>
                <div>
                  <Button size="slim">Edit</Button>
                </div>
              </div>
            </PlanLockOverlay>
          </IndexTable.Cell>
        </IndexTable.Row>
      );
    }
    return (
      <IndexTable.Row id={`${row.widgetId}-${row.eventId}`} key={`${row.widgetId}-${row.eventId}`} position={index}>
        <IndexTable.Cell>{title}</IndexTable.Cell>
        <IndexTable.Cell>{date}</IndexTable.Cell>
        <IndexTable.Cell>{status}</IndexTable.Cell>
        <IndexTable.Cell>{details}</IndexTable.Cell>
        <IndexTable.Cell>
          <Button size="slim" onClick={() => onEditEvent(row)}>
            Edit
          </Button>
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  return (
    <Page>
      {loading && allRows.length === 0 ? <PageLoader label="Loading events" /> : null}
      {allRows.length === 0 ? (
        <Card>
          <EmptyState
            heading="No events yet"
            action={{ content: "Create widget", onAction: onCreateWidget }}
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>
              Events aren&apos;t created on their own in {APP_DISPLAY_NAME} — first create a <strong>widget</strong>, then
              add events inside it (this app uses a widget-first flow rather than a flat event list).
            </p>
          </EmptyState>
        </Card>
      ) : (
        <Card padding="0">
          <Box padding="400">
            <InlineStack gap="300" wrap blockAlign="center">
              <div style={{ flex: "1 1 280px", minWidth: 220 }}>
                <TextField
                  label="Search events"
                  labelHidden
                  placeholder="Search event title, widget name, location…"
                  value={search}
                  onChange={setSearch}
                  prefix={<Icon source={SearchIcon} />}
                  autoComplete="off"
                  clearButton
                  onClearButtonClick={() => setSearch("")}
                />
              </div>
              <Popover
                active={filterOpen}
                onClose={() => setFilterOpen(false)}
                activator={
                  <Button
                    icon={FilterIcon}
                    onClick={() => setFilterOpen((v) => !v)}
                    disclosure
                  >
                    {filterLabel(filter)}
                  </Button>
                }
              >
                <ActionList actionRole="menuitemradio" items={filterItems} />
              </Popover>
            </InlineStack>
            <Box paddingBlockStart="200">
              <Text as="p" variant="bodySm" tone="subdued">
                Showing {visibleRows.length} of {allRows.length} event{allRows.length === 1 ? "" : "s"}
              </Text>
            </Box>
          </Box>
          {visibleRows.length === 0 ? (
            <Box padding="600">
              <Text as="p" tone="subdued" alignment="center">
                No events match your search or filter.
              </Text>
            </Box>
          ) : (
            <IndexTable
              itemCount={visibleRows.length}
              headings={[
                { title: "Event" },
                { title: "Date" },
                { title: "Status" },
                { title: "Widget / details" },
                { title: "" },
              ]}
              selectable={false}
            >
              {rowMarkup}
            </IndexTable>
          )}
        </Card>
      )}
    </Page>
  );
}
